import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { WarehouseSelect } from '../components/ui/WarehouseSelect';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  Tune as AdvancedIcon,
  MenuBook as DocsIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { api, endpoints, ensureUiScope } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import SuggestedActionsRail from '../components/SuggestedActionsRail';
import BringUpTimeline from '../components/bringup/BringUpTimeline';
import {
  BringUpCreateDeviceDialog,
  BringUpDangerZone,
  BringUpGuidedRecoveryDialog,
} from '../components/bringup/BringUpDialogs';
import {
  availabilityChipColor,
  buildGuidedRecoveryFailureNotice,
  canRunGuidedRecovery,
  Camera,
  CameraClaimOption,
  CommissionJob,
  DEFAULT_CLOUD_API_URL,
  DEFAULT_CLOUD_HOST,
  DEFAULT_CLOUD_MQTT_PORT,
  Device,
  EMPTY_BRING_UP_CREATE_FORM,
  GuidedRecoveryOptions,
  JobStep,
  RtspInlineCreds,
  RunnerProgress,
  StatusNotice,
  STEP_ORDER,
} from '../components/bringup/bringUpShared';
import { EnterpriseProductionDeployPanel } from '../components/enterpriseDeploy/EnterpriseProductionDeployPanel';
import { resolveEnterpriseEdgeHost } from '../components/enterpriseDeploy/enterpriseDeployShared';

const BringUp: React.FC = () => {
  const navigate = useNavigate();
  const { effectiveUser: user, isLoading: authLoading } = useAuth();
  const capabilities = Array.isArray((user as any)?.capabilities) ? (user as any).capabilities : [];
  const canWriteDevices = capabilities.includes('devices:write');
  const canReadDeployments = capabilities.includes('deployments:read');
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDeviceId = String(searchParams.get('device') || '').trim();

  const [devices, setDevices] = useState<Device[]>([]);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [loadingCameras, setLoadingCameras] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [edgeHost, setEdgeHost] = useState('');
  const [cloudHost, setCloudHost] = useState(DEFAULT_CLOUD_HOST);
  const [cloudApiUrl, setCloudApiUrl] = useState(DEFAULT_CLOUD_API_URL);
  const [cloudMqttPort, setCloudMqttPort] = useState(DEFAULT_CLOUD_MQTT_PORT);
  const [edgePassword, setEdgePassword] = useState('');
  const [edgeUser, setEdgeUser] = useState('');

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [bootstrapOnly, setBootstrapOnly] = useState(false);
  const [runOnboard, setRunOnboard] = useState(true);
  const [runSoftwareUpdate, setRunSoftwareUpdate] = useState(true);
  const [requireGatePass, setRequireGatePass] = useState(true);
  const [trustProbeLoading, setTrustProbeLoading] = useState(false);
  const [trustProbeResult, setTrustProbeResult] = useState<any | null>(null);
  const [claimOptions, setClaimOptions] = useState<CameraClaimOption[]>([]);
  const [selectedClaimOptionIds, setSelectedClaimOptionIds] = useState<string[]>([]);
  const [loadingClaimOptions, setLoadingClaimOptions] = useState(false);
  const [claimingCameras, setClaimingCameras] = useState(false);
  const [claimOptionsError, setClaimOptionsError] = useState<string | null>(null);
  const [claimResult, setClaimResult] = useState<{
    level: 'success' | 'warning' | 'error';
    message: string;
  } | null>(null);
  const [rtspCredsByOptionId, setRtspCredsByOptionId] = useState<Record<string, RtspInlineCreds>>({});
  const [claimedRtspCredsByCameraId, setClaimedRtspCredsByCameraId] = useState<Record<string, RtspInlineCreds>>({});
  const [showClaimedPasswordByCameraId, setShowClaimedPasswordByCameraId] = useState<Record<string, boolean>>({});
  const [savingClaimedCameraId, setSavingClaimedCameraId] = useState<string | null>(null);
  const [testingClaimedCameraId, setTestingClaimedCameraId] = useState<string | null>(null);
  const [enableManualClaims, setEnableManualClaims] = useState(false);
  const [manualClaimsJson, setManualClaimsJson] = useState('[]');

  const [job, setJob] = useState<CommissionJob | null>(null);
  const [runnerProgress, setRunnerProgress] = useState<RunnerProgress | null>(null);
  const [jobError, setJobError] = useState<string | null>(null);
  const [jobBusy, setJobBusy] = useState(false);
  const [decommissionConfirm, setDecommissionConfirm] = useState('');
  const [deleteAfterDecommission, setDeleteAfterDecommission] = useState(false);
  const [factoryResetOnDecommission, setFactoryResetOnDecommission] = useState(false);
  const [deleteConfigOnDecommission, setDeleteConfigOnDecommission] = useState(false);
  const [decommissionBusy, setDecommissionBusy] = useState(false);
  const [decommissionResult, setDecommissionResult] = useState<StatusNotice | null>(null);
  const [guidedRecoveryOpen, setGuidedRecoveryOpen] = useState(false);
  const [guidedRecoveryDeleteAfter, setGuidedRecoveryDeleteAfter] = useState(false);
  const [guidedRecoveryRetryBringUp, setGuidedRecoveryRetryBringUp] = useState(true);
  const [guidedRecoveryBusy, setGuidedRecoveryBusy] = useState(false);
  const [guidedRecoveryError, setGuidedRecoveryError] = useState<StatusNotice | null>(null);
  const [autoRecoverOnIdentityConflict, setAutoRecoverOnIdentityConflict] = useState(false);
  const [autoRecoveryArmedJobId, setAutoRecoveryArmedJobId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState(EMPTY_BRING_UP_CREATE_FORM);
  const uiScope = ensureUiScope();

  const selected = useMemo(
    () => devices.find((d) => d.device_id === selectedDeviceId) || null,
    [devices, selectedDeviceId],
  );
  const bringUpDeviceSelectOptions = useMemo(
    () =>
      devices.map((d) => ({
        value: d.device_id,
        label: d.name || d.device_id,
        secondary: d.device_id,
      })),
    [devices]
  );
  const clearSelectedDeviceState = useCallback(() => {
    setCameras([]);
    setJob(null);
    setRunnerProgress(null);
    setJobError(null);
    setTrustProbeResult(null);
    setClaimOptions([]);
    setSelectedClaimOptionIds([]);
    setRtspCredsByOptionId({});
    setClaimOptionsError(null);
    setClaimResult(null);
  }, []);

  const jobId = String(job?.job_id || job?.run_id || '').trim();
  const jobStatus = String(job?.status || '').toLowerCase();
  const jobIsRunning = jobStatus === 'running';
  const jobIsTerminal = jobStatus === 'succeeded' || jobStatus === 'failed' || jobStatus === 'cancelled';
  const provisionMode = String(job?.validation?.provision_mode || job?.provision_mode || '').trim().toLowerCase();
  const bootstrapOnlyJob = provisionMode === 'bootstrap_only';
  const bootstrapComplete = job?.validation?.bootstrap_complete === true;
  const readyForProduction = job?.validation?.ready_for_production === true;

  useEffect(() => {
    if (!bootstrapOnly) return;
    setRunOnboard(true);
    setRunSoftwareUpdate(false);
    setRequireGatePass(false);
  }, [bootstrapOnly]);

  useEffect(() => {
    if (!authLoading && user) void loadDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  useEffect(() => {
    const resolvedDeviceId = String(selected?.device_id || '').trim();
    if (!resolvedDeviceId) {
      clearSelectedDeviceState();
      return;
    }
    void loadCameras(resolvedDeviceId);
    void loadLatestJob(resolvedDeviceId);
    void loadCameraClaimOptions(resolvedDeviceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.device_id, clearSelectedDeviceState]);

  useEffect(() => {
    if (!selectedDeviceId || !jobId || !jobIsRunning) return;
    const timer = window.setInterval(() => {
      void refreshJob();
      void refreshRunnerProgress(selectedDeviceId);
    }, 3000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeviceId, jobId, jobIsRunning]);

  useEffect(() => {
    const resolved = resolveEnterpriseEdgeHost(selected);
    if (resolved) {
      setEdgeHost((prev) => (prev.trim() ? prev : resolved));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.device_id, selected?.ip_address, selected?.device_metadata]);

  const stepById = useMemo(() => {
    const map = new Map<string, JobStep>();
    for (const step of job?.steps || []) {
      const id = String(step?.step_id || '').trim();
      if (id) map.set(id, step);
    }
    return map;
  }, [job?.steps]);

  const failedStep = useMemo(
    () =>
      STEP_ORDER.map((stepId) => stepById.get(stepId) || null).find(
        (step) => String(step?.status || '').trim().toLowerCase() === 'failed',
      ) || null,
    [stepById],
  );

  useEffect(() => {
    if (!autoRecoveryArmedJobId || autoRecoveryArmedJobId !== jobId) return;
    if (jobStatus !== 'failed' || !canRunGuidedRecovery(failedStep) || guidedRecoveryBusy) return;
    setAutoRecoveryArmedJobId(null);
    setDecommissionResult({
      level: 'warning',
      message:
        `Bring Up detected stale edge identity or scope for ${selectedDeviceId || 'the selected device'}. ` +
        'Auto recovery is decommissioning the edge now and will retry Bring Up automatically if the wipe verifies clean.',
    });
    void runGuidedRecovery({
      deleteAfter: false,
      retryBringUp: true,
      closeDialog: false,
      source: 'auto',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRecoveryArmedJobId, failedStep, guidedRecoveryBusy, jobId, jobStatus, selectedDeviceId]);

  const claimableOptions = useMemo(
    () => claimOptions.filter((o) => !o.already_claimed),
    [claimOptions],
  );

  const unavailableClaimOptions = useMemo(
    () => claimableOptions.filter((o) => String(o.availability?.state || '').toLowerCase() === 'unavailable'),
    [claimableOptions],
  );

  const selectableClaimOptions = useMemo(
    () => claimableOptions.filter((o) => String(o.availability?.state || '').toLowerCase() !== 'unavailable'),
    [claimableOptions],
  );

  const claimedCameraRows = useMemo(
    () =>
      cameras.map((camera) => {
        const cameraId = String(camera.camera_id || '').trim();
        const source = camera.source && typeof camera.source === 'object' ? camera.source : {};
        const sourceType = String(source.type || '').trim().toLowerCase() || 'unknown';
        const optionMatch = claimOptions.find((opt) => String(opt.camera_id || '').trim() === cameraId);
        return {
          cameraId,
          displayName: String(camera.display_name || cameraId || 'Unnamed camera').trim(),
          sourceType,
          uri: String(source.uri || source.rtsp_url || '').trim(),
          credentialsStored: Boolean(source.credentials_ref),
          availabilityState: String(optionMatch?.availability?.state || '').trim().toLowerCase(),
          availabilityReason: String(optionMatch?.availability?.reason || '').trim(),
          healthStatus: String((camera.health || {}).status || '').trim().toLowerCase(),
          healthError: String((camera.health || {}).last_error || '').trim(),
        };
      }),
    [cameras, claimOptions],
  );

  async function loadDevices() {
    setLoadingDevices(true);
    setError(null);
    try {
      const res = await api.get(endpoints.devices.list);
      const items: Device[] = Array.isArray(res.data?.devices) ? res.data.devices : [];
      setDevices(items);
      if (selectedDeviceId && !items.some((d) => d.device_id === selectedDeviceId)) {
        clearSelectedDeviceState();
        setSearchParams({}, { replace: true });
        setError(
          `Selected device "${selectedDeviceId}" is not available in the current scope. Choose an existing device or add a new one.`,
        );
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load devices');
    } finally {
      setLoadingDevices(false);
    }
  }

  function openCreateDialog() {
    clearSelectedDeviceState();
    setSearchParams({}, { replace: true });
    setCreateError(null);
    setCreateForm(EMPTY_BRING_UP_CREATE_FORM);
    setCreateOpen(true);
  }

  function openGuidedRecoveryDialog() {
    setGuidedRecoveryDeleteAfter(false);
    setGuidedRecoveryRetryBringUp(true);
    setGuidedRecoveryError(null);
    setGuidedRecoveryOpen(true);
  }

  function closeGuidedRecoveryDialog() {
    if (guidedRecoveryBusy) return;
    setGuidedRecoveryOpen(false);
    setGuidedRecoveryDeleteAfter(false);
    setGuidedRecoveryRetryBringUp(true);
    setGuidedRecoveryError(null);
  }

  async function createDeviceFromBringUp() {
    const deviceId = String(createForm.device_id || '').trim();
    const name = String(createForm.name || '').trim();
    if (!deviceId || !name) {
      setCreateError('Device ID and Device Name are required.');
      return;
    }
    setCreateBusy(true);
    setCreateError(null);
    try {
      const scope = ensureUiScope();
      if (!scope.orgId || !scope.siteId) {
        setCreateError('UI scope is missing. Set Org/Site in the top-right scope selector before creating a device.');
        return;
      }
      await api.post(endpoints.devices.create, {
        device_id: deviceId,
        name,
        org_id: scope.orgId || undefined,
        site_id: scope.siteId || undefined,
        description: String(createForm.description || '').trim() || undefined,
        device_type: String(createForm.device_type || 'edge').trim() || 'edge',
        location: String(createForm.location || '').trim() || undefined,
        capabilities: {},
        device_metadata: {},
      });
      await loadDevices();
      setSearchParams({ device: deviceId }, { replace: true });
      setCreateOpen(false);
      setError(null);
    } catch (e: any) {
      setCreateError(
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'create_device_failed',
      );
    } finally {
      setCreateBusy(false);
    }
  }

  async function loadCameras(deviceId: string) {
    setLoadingCameras(true);
    try {
      const res = await api.get(`/api/v1/registry/devices/${encodeURIComponent(deviceId)}/cameras`);
      setCameras(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch {
      setCameras([]);
    } finally {
      setLoadingCameras(false);
    }
  }

  async function loadLatestJob(deviceId: string) {
    try {
      const res = await api.get(endpoints.commission.latest(deviceId));
      if (res.data?.error === 'no_jobs') {
        setJob(null);
        setRunnerProgress(null);
      } else {
        setJob(res.data || null);
        void refreshRunnerProgress(deviceId);
      }
    } catch {
      // keep page usable even when no prior jobs exist
    }
  }

  async function refreshRunnerProgress(deviceId: string) {
    try {
      const res = await api.get(`/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/update/latest`);
      const data = (res.data || {}) as RunnerProgress;
      if (data && (data.current_step || data.last_progress_at || data.status)) {
        setRunnerProgress(data);
      } else {
        setRunnerProgress(null);
      }
    } catch {
      setRunnerProgress(null);
    }
  }

  async function loadCameraClaimOptions(deviceId: string) {
    if (!deviceId) return;
    setLoadingClaimOptions(true);
    setClaimOptionsError(null);
    try {
      const res = await api.get(`/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/camera_claim_options`);
      const options: CameraClaimOption[] = Array.isArray(res.data?.options) ? res.data.options : [];
      setClaimOptions(options);
      const selectableIds = new Set(
        options
          .filter((opt) => String(opt.availability?.state || '').toLowerCase() !== 'unavailable')
          .map((opt) => String(opt.option_id || '').trim())
          .filter(Boolean),
      );
      setSelectedClaimOptionIds((prev) => prev.filter((id) => selectableIds.has(String(id || '').trim())));
      setRtspCredsByOptionId({});
    } catch (e: any) {
      setClaimOptions([]);
      setSelectedClaimOptionIds([]);
      setRtspCredsByOptionId({});
      setClaimOptionsError(e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'camera_claim_options_failed');
    } finally {
      setLoadingClaimOptions(false);
    }
  }

  function toggleClaimOption(optionId: string, checked: boolean) {
    setSelectedClaimOptionIds((prev) => {
      const set = new Set(prev);
      if (checked) set.add(optionId);
      else set.delete(optionId);
      return Array.from(set);
    });
  }

  function setRtspInlineCredential(optionId: string, key: 'username' | 'password', value: string) {
    const id = String(optionId || '').trim();
    if (!id) return;
    setRtspCredsByOptionId((prev) => {
      const existing = prev[id] || { username: '', password: '' };
      return {
        ...prev,
        [id]: {
          ...existing,
          [key]: value,
        },
      };
    });
  }

  function hasRtspInlineCredentials(optionId: string): boolean {
    const id = String(optionId || '').trim();
    if (!id) return false;
    const creds = rtspCredsByOptionId[id];
    return Boolean(String(creds?.username || '').trim() && String(creds?.password || '').trim());
  }

  function setClaimedRtspCredential(cameraId: string, key: 'username' | 'password', value: string) {
    const id = String(cameraId || '').trim();
    if (!id) return;
    setClaimedRtspCredsByCameraId((prev) => {
      const existing = prev[id] || { username: '', password: '' };
      return {
        ...prev,
        [id]: {
          ...existing,
          [key]: value,
        },
      };
    });
  }

  async function saveClaimedCameraCredentials(cameraId: string) {
    if (!selectedDeviceId) return;
    const id = String(cameraId || '').trim();
    const creds = claimedRtspCredsByCameraId[id] || { username: '', password: '' };
    const username = String(creds.username || '').trim();
    const password = String(creds.password || '').trim();
    if (!username || !password) {
      setClaimResult({ level: 'warning', message: `Enter both username and password for ${id}.` });
      return;
    }
    setSavingClaimedCameraId(id);
    setClaimResult(null);
    try {
      const res = await api.post(
        `/api/v1/registry/devices/${encodeURIComponent(selectedDeviceId)}/cameras/${encodeURIComponent(id)}/credentials`,
        { username, password },
      );
      const runtimeApplyOk = Boolean(res.data?.runtime_apply?.ok);
      const verificationOk = res.data?.verification ? Boolean(res.data?.verification?.ok) : null;
      const runtimeApplyError = String(
        res.data?.runtime_apply?.error ||
        res.data?.runtime_apply?.detail ||
        '',
      ).trim();
      const verificationError = String(
        res.data?.verification?.edge_result?.last_error ||
        res.data?.verification?.error ||
        res.data?.verification?.detail ||
        '',
      ).trim();

      if (!runtimeApplyOk) {
        setClaimResult({
          level: 'warning',
          message:
            `Saved RTSP credentials for ${id}, but edge runtime refresh did not complete.` +
            (runtimeApplyError ? ` ${runtimeApplyError}` : ''),
        });
      } else if (verificationOk === false) {
        setClaimResult({
          level: 'warning',
          message:
            `Saved RTSP credentials for ${id}, but strict verification still failed.` +
            (verificationError ? ` ${verificationError}` : ''),
        });
      } else {
        setClaimResult({
          level: 'success',
          message: `Updated RTSP credentials for ${id} and refreshed the edge runtime.`,
        });
      }
      await loadCameras(selectedDeviceId);
      await loadCameraClaimOptions(selectedDeviceId);
      setClaimedRtspCredsByCameraId((prev) => ({
        ...prev,
        [id]: { username, password: '' },
      }));
    } catch (e: any) {
      setClaimResult({
        level: 'error',
        message:
          e?.response?.data?.detail ||
          e?.response?.data?.error ||
          e?.message ||
          `Failed to update credentials for ${id}.`,
      });
    } finally {
      setSavingClaimedCameraId(null);
    }
  }

  async function testClaimedCameraFeed(cameraId: string) {
    if (!selectedDeviceId) return;
    const id = String(cameraId || '').trim();
    setTestingClaimedCameraId(id);
    setClaimResult(null);
    try {
      const res = await api.post(
        `/api/v1/registry/devices/${encodeURIComponent(selectedDeviceId)}/cameras/${encodeURIComponent(id)}/test_feed`,
        { force_probe: true, timeout_sec: 15, decode_seconds: 5 },
      );
      const ok = Boolean(res.data?.ok);
      const detail = String(
        res.data?.edge_result?.last_error ||
        res.data?.edge_result?.detail ||
        res.data?.detail ||
        res.data?.error ||
        res.data?.last_error ||
        (ok ? 'test_feed_passed' : 'test_feed_failed'),
      ).trim();
      setClaimResult({
        level: ok ? 'success' : 'warning',
        message: ok
          ? `Strict test feed passed for ${id}.`
          : `Strict test feed for ${id}: ${detail}`,
      });
      await loadCameras(selectedDeviceId);
      await loadCameraClaimOptions(selectedDeviceId);
    } catch (e: any) {
      setClaimResult({
        level: 'error',
        message:
          e?.response?.data?.detail ||
          e?.response?.data?.error ||
          e?.message ||
          `Test feed failed for ${id}.`,
      });
    } finally {
      setTestingClaimedCameraId(null);
    }
  }

  async function claimSelectedCameras() {
    if (!selectedDeviceId) return;
    setClaimingCameras(true);
    setClaimResult(null);
    setClaimOptionsError(null);
    try {
      const optionClaims = claimOptions
        .filter(
          (opt) =>
            selectedClaimOptionIds.includes(String(opt.option_id || '')) &&
            !opt.already_claimed &&
            String(opt.availability?.state || '').toLowerCase() !== 'unavailable',
        )
        .map((opt) => {
          const baseClaim: Record<string, any> = opt.claim_payload || {
            camera_id: opt.camera_id,
            display_name: opt.display_name || opt.camera_id,
            slug: opt.camera_id,
            source: opt.source || {},
          };
          const sourceType = String(opt.source?.type || baseClaim?.source?.type || '').toLowerCase();
          if (sourceType !== 'rtsp') return baseClaim;

          const optionId = String(opt.option_id || '').trim();
          const inlineCreds = rtspCredsByOptionId[optionId] || { username: '', password: '' };
          const username = String(inlineCreds.username || '').trim();
          const password = String(inlineCreds.password || '').trim();
          if (!username || !password) return baseClaim;

          const nextSource = {
            ...(typeof baseClaim.source === 'object' && baseClaim.source ? baseClaim.source : {}),
            type: 'rtsp',
            auth: { username, password },
          };
          return {
            ...baseClaim,
            source: nextSource,
            credentials: { username, password },
          };
        });

      let manualClaims: any[] = [];
      if (enableManualClaims) {
        const parsed = JSON.parse(String(manualClaimsJson || '[]'));
        if (!Array.isArray(parsed)) throw new Error('manual camera claims JSON must be an array');
        manualClaims = parsed;
      }

      const cameraClaims = [...optionClaims, ...manualClaims];
      if (cameraClaims.length === 0) {
        const unavailableSelected = claimOptions.some(
          (opt) =>
            selectedClaimOptionIds.includes(String(opt.option_id || '')) &&
            String(opt.availability?.state || '').toLowerCase() === 'unavailable',
        );
        setClaimResult({
          level: 'warning',
          message: unavailableSelected
            ? 'Selected cameras are currently unavailable and were not submitted. Reconnect the camera or choose an RTSP option.'
            : 'No cameras selected to claim.',
        });
        return;
      }

      const res = await api.post(
        `/api/v1/pipeline/devices/${encodeURIComponent(selectedDeviceId)}/edge/camera_claims`,
        {
          camera_claims: cameraClaims,
          require_rtsp_auth: true,
          precheck_availability: true,
          run_registry_deploy: true,
          run_desired_state_sync: true,
        },
      );
      const claimed = Number(res.data?.claim_result?.claimed || cameraClaims.length);
      setClaimResult({ level: 'success', message: `Claimed ${claimed} camera(s) and synced desired state.` });
      await loadCameras(selectedDeviceId);
      await loadCameraClaimOptions(selectedDeviceId);
      setEnableManualClaims(false);
      setManualClaimsJson('[]');
      setRtspCredsByOptionId({});
    } catch (e: any) {
      const detail =
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        e?.message ||
        'camera_claim_failed';
      setClaimResult({ level: 'error', message: String(detail) });
    } finally {
      setClaimingCameras(false);
    }
  }

  function buildBringUpPayload(): Record<string, any> {
    const payload: Record<string, any> = {
      edge_host: edgeHost.trim() || undefined,
      edge_user: edgeUser.trim() || undefined,
      edge_password: edgePassword.trim() || undefined,
      bootstrap_only: bootstrapOnly,
      run_onboard: runOnboard,
      run_software_update: runSoftwareUpdate,
      update_components: ['native', 'docker'],
      run_registry_deploy: true,
      run_desired_state_sync: true,
      require_preflight_pass: requireGatePass,
      require_gate_pass: requireGatePass,
      setup_payload: { auto_fix: true },
      preflight_payload: {},
    };
  if (showAdvanced) {
    payload.cloud_host = cloudHost.trim() || undefined;
    payload.cloud_api_url = cloudApiUrl.trim() || undefined;
    payload.cloud_mqtt_port = cloudMqttPort.trim() || undefined;
  }
    Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
    return payload;
  }

  async function startBringUpForDevice(
    deviceIdOverride?: string,
    options?: { armAutoRecovery?: boolean },
  ) {
    const targetDeviceId = String(deviceIdOverride || selectedDeviceId || '').trim();
    if (!targetDeviceId) return;
    setJobBusy(true);
    setJobError(null);
    try {
      const startEndpoint = bootstrapOnly
        ? endpoints.commission.bootstrapIdentity(targetDeviceId)
        : endpoints.commission.fromZero(targetDeviceId);
      const res = await api.post(
        startEndpoint,
        buildBringUpPayload(),
      );
      const nextJob = res.data || null;
      setJob(nextJob);
      setRunnerProgress(null);
      const nextJobId = String(nextJob?.job_id || nextJob?.run_id || '').trim();
      setAutoRecoveryArmedJobId(options?.armAutoRecovery && nextJobId ? nextJobId : null);
    } catch (e: any) {
      setAutoRecoveryArmedJobId(null);
      setJobError(e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'bringup_start_failed');
    } finally {
      setJobBusy(false);
    }
  }

  async function runBootstrapTrustProbe() {
    const targetDeviceId = String(selectedDeviceId || '').trim();
    if (!targetDeviceId) return;
    setTrustProbeLoading(true);
    setTrustProbeResult(null);
    try {
      const res = await api.post(
        endpoints.commission.bootstrapTrustProbe(targetDeviceId),
        buildBringUpPayload(),
      );
      setTrustProbeResult(res.data || null);
    } catch (e: any) {
      setTrustProbeResult(
        e?.response?.data || {
          ok: false,
          error: e?.message || 'bootstrap_trust_probe_failed',
        },
      );
    } finally {
      setTrustProbeLoading(false);
    }
  }

  function handleStartBringUp() {
    void startBringUpForDevice(undefined, {
      armAutoRecovery: autoRecoverOnIdentityConflict,
    });
  }

  async function refreshJob() {
    if (!selectedDeviceId || !jobId) return;
    try {
      const res = await api.get(endpoints.commission.status(selectedDeviceId, jobId));
      setJob(res.data || null);
      void refreshRunnerProgress(selectedDeviceId);
    } catch (e: any) {
      setJobError(e?.response?.data?.error || e?.message || 'refresh_failed');
    }
  }

  async function cancelJob() {
    if (!selectedDeviceId || !jobId) return;
    try {
      await api.post(endpoints.commission.cancel(selectedDeviceId, jobId), {});
      await refreshJob();
    } catch (e: any) {
      setJobError(e?.response?.data?.error || e?.message || 'cancel_failed');
    }
  }

  async function runDecommission() {
    if (!selectedDeviceId || !selected) return;
    const expected = selected.device_id;
    const currentEdgeHost = String(edgeHost || selected?.ip_address || '').trim();
    if (String(decommissionConfirm || '').trim() !== expected) {
      setDecommissionResult({
        level: 'error',
        message: `Type "${expected}" to confirm decommission.`,
      });
      return;
    }
    if (!currentEdgeHost) {
      setDecommissionResult({
        level: 'error',
        message: 'Current Edge Host/IP is required before decommission can verify and wipe the edge.',
      });
      return;
    }
    setDecommissionBusy(true);
    setDecommissionResult(null);
    try {
      const decommRes = await api.post(
        `/api/v1/provisioning/devices/${encodeURIComponent(selectedDeviceId)}/decommission`,
        {
          edge_host: currentEdgeHost,
          factory_reset: factoryResetOnDecommission,
          delete_device_scoped_config: deleteConfigOnDecommission,
        },
      );
      const notified = decommRes?.data?.mqtt_notified === true;
      const edgeConfirmed = decommRes?.data?.edge_apply_confirmed === true;
      const mqttError = String(decommRes?.data?.mqtt_error || '').trim();
      if (deleteAfterDecommission) {
        if (!edgeConfirmed) {
          setDecommissionResult({
            level: 'warning',
            message:
              `Cloud credentials were revoked, but edge apply is unconfirmed. ` +
              `Not deleting cloud device record yet. Verify edge wipe first.`,
          });
          return;
        }
        await api.delete(`/api/v1/devices/${encodeURIComponent(selectedDeviceId)}`);
        const listRes = await api.get(endpoints.devices.list);
        const nextItems: Device[] = Array.isArray(listRes.data?.devices) ? listRes.data.devices : [];
        setDevices(nextItems);
        const next = nextItems.find((d) => d.device_id !== selectedDeviceId);
        if (next) {
          setSearchParams({ device: next.device_id }, { replace: true });
        } else {
          setSearchParams({}, { replace: true });
          setJob(null);
          setCameras([]);
        }
        setDecommissionResult({
          level: edgeConfirmed ? 'success' : 'warning',
          message: edgeConfirmed
            ? `Device ${selectedDeviceId} decommissioned (edge confirmed) and deleted.`
            : `Device ${selectedDeviceId} deleted. Edge notify failed (${mqttError || 'offline'}).`,
        });
      } else {
        setDecommissionResult({
          level: edgeConfirmed ? 'success' : 'warning',
          message: edgeConfirmed
            ? `Device ${selectedDeviceId} decommissioned (edge confirmed) and left claimable for reprovision.`
            : notified
              ? `Cloud credentials revoked for ${selectedDeviceId}, but edge apply is unconfirmed. Verify edge wipe before re-onboarding.`
              : `Cloud credentials revoked for ${selectedDeviceId}, but edge notify failed (${mqttError || 'offline'}).`,
        });
      }
      setDecommissionConfirm('');
      setDeleteAfterDecommission(false);
      setFactoryResetOnDecommission(false);
      setDeleteConfigOnDecommission(false);
    } catch (e: any) {
      setDecommissionResult({
        level: 'error',
        message:
          e?.response?.data?.detail ||
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'decommission_failed',
      });
    } finally {
      setDecommissionBusy(false);
    }
  }

  async function runDeleteDeviceOnly() {
    if (!selectedDeviceId || !selected) return;
    const expected = selected.device_id;
    if (String(decommissionConfirm || '').trim() !== expected) {
      setDecommissionResult({
        level: 'error',
        message: `Type "${expected}" to confirm delete.`,
      });
      return;
    }
    setDecommissionBusy(true);
    setDecommissionResult(null);
    try {
      await api.delete(`/api/v1/devices/${encodeURIComponent(selectedDeviceId)}`);
      const listRes = await api.get(endpoints.devices.list);
      const nextItems: Device[] = Array.isArray(listRes.data?.devices) ? listRes.data.devices : [];
      setDevices(nextItems);
      const next = nextItems.find((d) => d.device_id !== selectedDeviceId);
      if (next) {
        setSearchParams({ device: next.device_id }, { replace: true });
      } else {
        setSearchParams({}, { replace: true });
        setJob(null);
        setCameras([]);
      }
      setDecommissionResult({
        level: 'success',
        message: `Device ${selectedDeviceId} deleted from cloud registry.`,
      });
      setDecommissionConfirm('');
      setDeleteAfterDecommission(false);
      setFactoryResetOnDecommission(false);
      setDeleteConfigOnDecommission(false);
    } catch (e: any) {
      setDecommissionResult({
        level: 'error',
        message:
          e?.response?.data?.detail ||
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'delete_device_failed',
      });
    } finally {
      setDecommissionBusy(false);
    }
  }

  async function runGuidedRecovery(options: GuidedRecoveryOptions = {}) {
    if (!selectedDeviceId || !selected) return;
    const {
      deleteAfter = guidedRecoveryDeleteAfter,
      retryBringUp = guidedRecoveryRetryBringUp,
      closeDialog = true,
      source = 'manual',
    } = options;
    const currentEdgeHost = String(edgeHost || selected?.ip_address || '').trim();
    const reportError = (message: string, payload?: Record<string, any> | null) => {
      const notice = buildGuidedRecoveryFailureNotice(message, payload);
      if (source === 'manual') {
        setGuidedRecoveryError(notice);
      } else {
        setJobError(notice.message);
      }
      setDecommissionResult(notice);
    };
    if (!currentEdgeHost) {
      reportError(
        'Current Edge Host/IP is required before guided recovery can verify and wipe the edge.',
      );
      return;
    }

    setGuidedRecoveryBusy(true);
    setGuidedRecoveryError(null);

    try {
      const decommRes = await api.post(
        `/api/v1/provisioning/devices/${encodeURIComponent(selectedDeviceId)}/decommission`,
        {
          edge_host: currentEdgeHost,
          factory_reset: true,
          delete_device_scoped_config: true,
        },
      );
      const notified = decommRes?.data?.mqtt_notified === true;
      const edgeConfirmed = decommRes?.data?.edge_apply_confirmed === true;
      const mqttError = String(decommRes?.data?.mqtt_error || '').trim();

      if (!edgeConfirmed) {
        reportError(
          notified
            ? 'Cloud credentials were revoked, but edge apply is unconfirmed. Verify the edge wipe before retrying Bring Up.'
            : `Cloud credentials were revoked, but edge notify failed (${mqttError || 'offline'}).`,
          decommRes?.data,
        );
        return;
      }

      if (!deleteAfter) {
        await api.post(endpoints.devices.ensure, {
          device_id: selectedDeviceId,
          org_id: uiScope.orgId || undefined,
          site_id: uiScope.siteId || undefined,
        });
        await loadDevices();
        setSearchParams({ device: selectedDeviceId }, { replace: true });

        if (retryBringUp) {
          if (closeDialog) {
            setGuidedRecoveryOpen(false);
            setGuidedRecoveryDeleteAfter(false);
            setGuidedRecoveryRetryBringUp(true);
          }
          setDecommissionResult({
            level: 'success',
            message: `Factory reset confirmed for ${selectedDeviceId}. Recreated the selected cloud device and restarting Bring Up now.`,
          });
          await startBringUpForDevice(selectedDeviceId);
          return;
        }
      } else {
        await api.delete(`/api/v1/devices/${encodeURIComponent(selectedDeviceId)}`);
        const listRes = await api.get(endpoints.devices.list);
        const nextItems: Device[] = Array.isArray(listRes.data?.devices) ? listRes.data.devices : [];
        setDevices(nextItems);
        const next = nextItems.find((d) => d.device_id !== selectedDeviceId);
        if (next) {
          setSearchParams({ device: next.device_id }, { replace: true });
        } else {
          setSearchParams({}, { replace: true });
          setJob(null);
          setCameras([]);
        }
      }

      setDecommissionResult({
        level: 'success',
        message: deleteAfter
          ? `Guided recovery completed for ${selectedDeviceId}. Edge confirmed the factory reset request and the selected cloud device remains deleted.`
          : `Guided recovery completed for ${selectedDeviceId}. Edge confirmed the factory reset request and recreated the selected cloud device.`,
      });

      if (closeDialog) {
        setGuidedRecoveryOpen(false);
        setGuidedRecoveryDeleteAfter(false);
        setGuidedRecoveryRetryBringUp(true);
      }
    } catch (e: any) {
      reportError(
        e?.response?.data?.detail ||
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'guided_recovery_failed',
        e?.response?.data,
      );
    } finally {
      setGuidedRecoveryBusy(false);
    }
  }

  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="320px">
        <CircularProgress />
      </Box>
    );
  }
  if (!user) {
    return (
      <Alert severity="warning">Please log in to start device bring-up.</Alert>
    );
  }

  return (
    <Box>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} lg={8}>
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" mb={2} gap={1}>
            <Box>
              <Typography variant="h4">Bring Up Device</Typography>
              <Typography variant="body2" color="text.secondary">
                Setup commissioning entry point for registration, onboarding, strict preflight, and the initial device gate
                before cameras, zones, and pack-driven policies are staged.
              </Typography>
            </Box>
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Tooltip title="Open documentation (Setup options)">
                <IconButton
                  size="small"
                  component="a"
                  href="/docs/user-guide/setup-options/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <DocsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button size="small" variant="outlined" onClick={() => navigate('/devices')}>
                Claim / Manage Devices
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate(selectedDeviceId ? `/preflight?device=${encodeURIComponent(selectedDeviceId)}` : '/preflight')}
              >
                Advanced Preflight
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate(selectedDeviceId ? `/devices/${encodeURIComponent(selectedDeviceId)}/control` : '/devices/control')}
              >
                Device Control
              </Button>
            </Stack>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {!canWriteDevices && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You have read-only device access. Bring-up mutations are disabled.
            </Alert>
          )}

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              1. Select Device
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <WarehouseSelect
                  label="Device"
                  value={selectedDeviceId}
                  onChange={(deviceId) => setSearchParams({ device: deviceId })}
                  options={bringUpDeviceSelectOptions}
                  emptyOption={{ value: '', label: 'Select an existing device' }}
                  disabled={loadingDevices}
                  minWidth="100%"
                  formControlSx={{ width: '100%' }}
                />
                <Stack direction="row" gap={1} mt={1} flexWrap="wrap">
                  <Button size="small" variant="outlined" onClick={openCreateDialog} disabled={!canWriteDevices || loadingDevices}>
                    Add New Device
                  </Button>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Current Edge Host/IP"
                  value={edgeHost}
                  onChange={(e) => setEdgeHost(e.target.value)}
                  placeholder="e.g. edge-host or IP"
                  helperText="Used only as a current network target; device identity remains canonical device_id."
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Edge SSH user"
                  value={edgeUser}
                  onChange={(e) => setEdgeUser(e.target.value)}
                  placeholder="root"
                  helperText="Stock Ubuntu images: ubuntu. Empty defaults to root."
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  label="Edge SSH password (optional)"
                  value={edgePassword}
                  onChange={(e) => setEdgePassword(e.target.value)}
                  placeholder="first boot / password auth"
                  helperText="Only if deploy runner must use password SSH once to install keys."
                />
              </Grid>
            </Grid>
          </Paper>

      <BringUpCreateDeviceDialog
        open={createOpen}
        busy={createBusy}
        error={createError}
        form={createForm}
        setForm={setCreateForm}
        canWriteDevices={canWriteDevices}
        uiScope={uiScope}
        onClose={() => setCreateOpen(false)}
        onClearError={() => setCreateError(null)}
        onCreate={createDeviceFromBringUp}
      />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6">2. Bring-up Configuration</Typography>
          <FormControlLabel
            control={<Switch checked={showAdvanced} onChange={(_, checked) => setShowAdvanced(checked)} />}
            label={
              <Stack direction="row" alignItems="center" gap={0.5}>
                <AdvancedIcon fontSize="small" />
                <span>Advanced</span>
              </Stack>
            }
          />
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={bootstrapOnly}
                  onChange={(_, checked) => setBootstrapOnly(checked)}
                />
              }
              label="Bootstrap identity only (trust + claim + credentials, no runtime install yet)"
            />
            <Typography variant="body2" color="text.secondary">
              Use this for factory-fresh or wiped units when you want a safe first pass that stops after onboarding.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Cloud Host (DNS)"
              value={cloudHost}
              onChange={(e) => setCloudHost(e.target.value)}
              disabled={!showAdvanced}
              helperText={showAdvanced ? 'Advanced override only. Use DNS hostname that matches TLS cert SAN.' : 'Auto-managed. Enable Advanced to override.'}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Cloud API URL"
              value={cloudApiUrl}
              onChange={(e) => setCloudApiUrl(e.target.value)}
              disabled={!showAdvanced}
              helperText={showAdvanced ? 'Advanced override only. Must match certificate hostname.' : 'Auto-managed. Enable Advanced to override.'}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Cloud MQTT TLS Port"
              value={cloudMqttPort}
              onChange={(e) => setCloudMqttPort(e.target.value)}
              disabled={!showAdvanced}
              placeholder={DEFAULT_CLOUD_MQTT_PORT}
              helperText={showAdvanced ? 'Advanced override only.' : 'Auto-managed. Enable Advanced to override.'}
            />
          </Grid>
          {showAdvanced && (
            <>
              <Grid item xs={12} md={6}>
                <Stack direction="row" gap={2} flexWrap="wrap" sx={{ pt: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={runOnboard}
                        onChange={(_, checked) => setRunOnboard(checked)}
                        disabled={bootstrapOnly}
                      />
                    }
                    label="Run onboard step"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={runSoftwareUpdate}
                        onChange={(_, checked) => setRunSoftwareUpdate(checked)}
                        disabled={bootstrapOnly}
                      />
                    }
                    label="Install latest runtime"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={requireGatePass}
                        onChange={(_, checked) => setRequireGatePass(checked)}
                        disabled={bootstrapOnly}
                      />
                    }
                    label="Require gate pass"
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  {bootstrapOnly
                    ? 'Bootstrap-only mode skips runtime install, desired-state sync, camera claims, and production gates. Run full Bring Up after bootstrap completes.'
                    : 'Camera claim is handled as a separate post-provision action in Step 4. This prevents accidental claims during base unit bring-up.'}
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={runBootstrapTrustProbe}
                    disabled={!selectedDeviceId || trustProbeLoading || jobBusy || guidedRecoveryBusy}
                  >
                    {trustProbeLoading ? 'Validating trust…' : 'Validate Cloud Trust'}
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    Verifies that the onboarding CA bundle matches the active cloud <code>:8443</code> certificate chain before bootstrap.
                  </Typography>
                </Stack>
                {trustProbeResult && (
                  <Alert severity={trustProbeResult.ok ? 'success' : 'warning'} sx={{ mt: 1.5 }}>
                    <Typography variant="subtitle2">
                      {trustProbeResult.ok ? 'Cloud trust verified' : 'Cloud trust check failed'}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {trustProbeResult.ok
                        ? `Verified ${String(trustProbeResult.cloud_api_url || 'cloud endpoint')} using ${String(trustProbeResult.ca_cert_path || 'configured onboarding CA')}.`
                        : String(
                            trustProbeResult.recommended_action ||
                              trustProbeResult.detail ||
                              trustProbeResult.error ||
                              'bootstrap_trust_probe_failed',
                          )}
                    </Typography>
                    <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                      {trustProbeResult.server_hostname && (
                        <Chip size="small" variant="outlined" label={`server: ${trustProbeResult.server_hostname}`} />
                      )}
                      {trustProbeResult.connect_host && (
                        <Chip size="small" variant="outlined" label={`connect: ${trustProbeResult.connect_host}:${trustProbeResult.port || 8443}`} />
                      )}
                      {trustProbeResult.failure_family && (
                        <Chip size="small" variant="outlined" color="warning" label={`failure: ${trustProbeResult.failure_family}`} />
                      )}
                    </Stack>
                  </Alert>
                )}
              </Grid>
            </>
          )}
        </Grid>

        {jobError && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setJobError(null)}>
            {jobError}
          </Alert>
        )}

        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Checkbox
              checked={autoRecoverOnIdentityConflict}
              onChange={(e) => setAutoRecoverOnIdentityConflict(e.target.checked)}
              disabled={!canWriteDevices || !selectedDeviceId || jobBusy || jobIsRunning || guidedRecoveryBusy}
            />
          }
          label="If Bring Up hits stale identity or stale scope, decommission the edge and retry automatically"
        />
        {autoRecoverOnIdentityConflict && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Auto recovery requires `Current Edge Host/IP` and will factory-reset the edge, wipe device-scoped cloud
            config, recreate the selected device, and restart Bring Up automatically.
          </Alert>
        )}

        <Stack direction="row" gap={1} mt={2} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={<PlayIcon />}
            onClick={handleStartBringUp}
            disabled={!canWriteDevices || !selectedDeviceId || jobBusy || jobIsRunning || guidedRecoveryBusy}
          >
            {jobBusy
              ? 'Starting…'
              : guidedRecoveryBusy
                ? 'Recovering…'
                : jobIsRunning
                  ? 'Running…'
                  : bootstrapOnly
                    ? 'Start Identity Bootstrap'
                    : 'Start Bring-up'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refreshJob}
            disabled={!jobId || jobBusy || guidedRecoveryBusy}
          >
            Refresh Status
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<CancelIcon />}
            onClick={cancelJob}
            disabled={!canWriteDevices || !jobId || jobIsTerminal || jobBusy || guidedRecoveryBusy}
          >
            Cancel
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          3. Bring-up Timeline
        </Typography>
        <BringUpTimeline
          job={job}
          jobId={jobId}
          jobStatus={jobStatus}
          jobIsRunning={jobIsRunning}
          runnerProgress={runnerProgress}
          stepById={stepById}
          readyForProduction={readyForProduction}
          bootstrapOnlyJob={bootstrapOnlyJob}
          bootstrapComplete={bootstrapComplete}
          failedStep={failedStep}
          canWriteDevices={canWriteDevices}
          selectedDeviceId={selectedDeviceId}
          openGuidedRecoveryDialog={openGuidedRecoveryDialog}
          onOpenDevices={() => navigate('/devices')}
        />
      </Paper>

      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          4. Add Cameras (Optional, Post-Provision)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          After the unit is provisioned, explicitly claim cameras. `hardware_cam` is device-bound (CSI)
          and RTSP claims require credentials + availability precheck.
        </Typography>
        <Stack direction="row" gap={1} mb={1.5} flexWrap="wrap">
          <Button
            variant="outlined"
            size="small"
            onClick={() => selectedDeviceId && loadCameraClaimOptions(selectedDeviceId)}
            disabled={!canWriteDevices || !selectedDeviceId || loadingClaimOptions || jobIsRunning}
          >
            {loadingClaimOptions ? 'Refreshing…' : 'Refresh Camera Options'}
          </Button>
          <Chip size="small" label={`Claimed: ${cameras.length}`} color="default" />
          <Chip size="small" label={`Claimable: ${selectableClaimOptions.length}`} color="default" />
          {unavailableClaimOptions.length > 0 && (
            <Chip size="small" label={`Unavailable: ${unavailableClaimOptions.length}`} color="warning" />
          )}
          {loadingCameras && <Chip size="small" label="Loading claimed cameras…" color="info" />}
        </Stack>
        {unavailableClaimOptions.some((opt) => String(opt.camera_id || '').trim().toLowerCase() === 'hardware_cam') && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            `hardware_cam` is currently unplugged or unsupported on this edge, so it is shown for context but cannot be claimed right now.
          </Alert>
        )}
        {claimOptionsError && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setClaimOptionsError(null)}>
            {claimOptionsError}
          </Alert>
        )}
        {claimResult && (
          <Alert severity={claimResult.level} sx={{ mb: 1.5 }} onClose={() => setClaimResult(null)}>
            {claimResult.message}
          </Alert>
        )}
        <List dense sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1.5 }}>
          {claimedCameraRows.length === 0 ? (
            <ListItem>
              <ListItemText
                primary="No cameras claimed on this device yet."
                secondary="Once a camera is claimed, it will appear here with its camera ID and source details."
              />
            </ListItem>
          ) : (
            claimedCameraRows.map((camera) => (
              <ListItem key={`claimed-${camera.cameraId}`} divider>
                <ListItemText
                  primary={
                    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {camera.displayName}
                      </Typography>
                      <Chip size="small" label={camera.cameraId} />
                      <Chip size="small" label={camera.sourceType} />
                      {camera.credentialsStored && <Chip size="small" label="credentials stored" color="success" />}
                      {camera.availabilityState && (
                        <Chip
                          size="small"
                          label={camera.availabilityState}
                          color={availabilityChipColor(camera.availabilityState)}
                        />
                      )}
                    </Stack>
                  }
                  secondary={
                    <Box component="span" sx={{ display: 'block' }}>
                      <Box component="span" sx={{ display: 'block' }}>
                        {camera.uri || 'No URI stored'}
                      </Box>
                      {camera.healthStatus && (
                        <Box component="span" sx={{ display: 'block' }}>
                          Health: {camera.healthStatus}
                          {camera.healthError ? ` • ${camera.healthError}` : ''}
                        </Box>
                      )}
                      {camera.availabilityReason && (
                        <Box component="span" sx={{ display: 'block' }}>
                          {camera.availabilityReason}
                        </Box>
                      )}
                      {camera.sourceType === 'rtsp' && (
                        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ mt: 1, maxWidth: 820 }}>
                          <TextField
                            size="small"
                            label="RTSP Username"
                            value={claimedRtspCredsByCameraId[camera.cameraId]?.username || ''}
                            onChange={(e) => setClaimedRtspCredential(camera.cameraId, 'username', e.target.value)}
                            disabled={!canWriteDevices || jobIsRunning || savingClaimedCameraId === camera.cameraId}
                            placeholder="admin"
                          />
                          <TextField
                            size="small"
                            type={showClaimedPasswordByCameraId[camera.cameraId] ? 'text' : 'password'}
                            label="RTSP Password"
                            value={claimedRtspCredsByCameraId[camera.cameraId]?.password || ''}
                            onChange={(e) => setClaimedRtspCredential(camera.cameraId, 'password', e.target.value)}
                            disabled={!canWriteDevices || jobIsRunning || savingClaimedCameraId === camera.cameraId}
                            placeholder="Enter new password"
                            helperText="Stored passwords are hidden. Enter a new password to replace the current one."
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    edge="end"
                                    size="small"
                                    onClick={() =>
                                      setShowClaimedPasswordByCameraId((prev) => ({
                                        ...prev,
                                        [camera.cameraId]: !prev[camera.cameraId],
                                      }))
                                    }
                                  >
                                    {showClaimedPasswordByCameraId[camera.cameraId] ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => saveClaimedCameraCredentials(camera.cameraId)}
                            disabled={!canWriteDevices || jobIsRunning || savingClaimedCameraId === camera.cameraId}
                          >
                            {savingClaimedCameraId === camera.cameraId ? 'Saving…' : 'Save Credentials'}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => testClaimedCameraFeed(camera.cameraId)}
                            disabled={!canWriteDevices || jobIsRunning || testingClaimedCameraId === camera.cameraId}
                          >
                            {testingClaimedCameraId === camera.cameraId ? 'Testing…' : 'Test Feed'}
                          </Button>
                        </Stack>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))
          )}
        </List>
        <List dense sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1.5 }}>
          {claimableOptions.length === 0 && (
            <ListItem>
              <ListItemText
                primary={claimOptions.length === 0 ? 'No camera claim options found yet.' : 'No claimable cameras.'}
                secondary={
                  claimOptions.length === 0
                    ? 'Run bring-up first, then refresh camera options.'
                    : 'All available cameras are already claimed by other devices. Unclaim from the other device first.'
                }
              />
            </ListItem>
          )}
          {claimableOptions.map((opt) => {
            const optionId = String(opt.option_id || '');
            const checked = selectedClaimOptionIds.includes(optionId);
            const sourceType = String(opt.source?.type || '').toLowerCase();
            const state = String(opt.availability?.state || 'unknown');
            const claimReady = opt.claim_ready !== false;
            const hasInlineCreds = sourceType === 'rtsp' && hasRtspInlineCredentials(optionId);
            const effectiveClaimReady = claimReady || hasInlineCreds;
            return (
              <ListItem
                key={optionId}
                divider
                secondaryAction={
                  <Checkbox
                    edge="end"
                    checked={checked}
                    onChange={(e) => toggleClaimOption(optionId, e.target.checked)}
                    disabled={!canWriteDevices || Boolean(opt.already_claimed) || jobIsRunning || state === 'unavailable' || !effectiveClaimReady}
                  />
                }
              >
                <ListItemText
                  primary={
                    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {opt.display_name || opt.camera_id}
                      </Typography>
                      <Chip size="small" label={sourceType || 'unknown'} />
                      <Chip
                        size="small"
                        label={state}
                        color={availabilityChipColor(state)}
                      />
                      {opt.already_claimed && <Chip size="small" label="already claimed" color="success" />}
                      {sourceType === 'hardware' && <Chip size="small" label="device-bound CSI" color="info" />}
                      {!claimReady && <Chip size="small" label={opt.claim_block_reason || 'claim blocked'} color="warning" />}
                      {sourceType === 'rtsp' && hasInlineCreds && <Chip size="small" label="credentials entered" color="success" />}
                    </Stack>
                  }
                  secondary={
                    <Box component="span" sx={{ display: 'block' }}>
                      <Box component="span" sx={{ display: 'block' }}>
                        {opt.camera_id}
                        {opt.availability?.reason ? ` • ${opt.availability.reason}` : ''}
                        {sourceType === 'rtsp' ? ' • enter credentials below' : ''}
                      </Box>
                      {sourceType === 'rtsp' && !opt.already_claimed && (
                        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ mt: 1, maxWidth: 520 }}>
                          <TextField
                            size="small"
                            label="RTSP Username"
                            value={rtspCredsByOptionId[optionId]?.username || ''}
                            onChange={(e) => setRtspInlineCredential(optionId, 'username', e.target.value)}
                            disabled={!canWriteDevices || jobIsRunning}
                          />
                          <TextField
                            size="small"
                            type="password"
                            label="RTSP Password"
                            value={rtspCredsByOptionId[optionId]?.password || ''}
                            onChange={(e) => setRtspInlineCredential(optionId, 'password', e.target.value)}
                            disabled={!canWriteDevices || jobIsRunning}
                          />
                        </Stack>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            );
          })}
        </List>
        <FormControlLabel
          control={
            <Switch
              checked={enableManualClaims}
              onChange={(_, checked) => setEnableManualClaims(checked)}
              disabled={!canWriteDevices || jobIsRunning}
            />
          }
          label="Advanced: manual camera claims JSON"
        />
        {enableManualClaims && (
          <TextField
            fullWidth
            multiline
            minRows={6}
            sx={{ mt: 1 }}
            value={manualClaimsJson}
            onChange={(e) => setManualClaimsJson(e.target.value)}
            helperText='JSON array of claim objects. For RTSP use source.uri + source.auth {username,password}.'
          />
        )}
        <Stack direction="row" gap={1} mt={2} flexWrap="wrap">
          <Button
            variant="contained"
            onClick={claimSelectedCameras}
            disabled={!canWriteDevices || !selectedDeviceId || jobIsRunning || claimingCameras}
          >
            {claimingCameras ? 'Claiming…' : 'Claim Selected Cameras'}
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          5. Finish
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Bring-up ends at commissioning. Continue in Setup to verify cameras, define zones, and stage the first pack and
          policy for this scope.
        </Typography>
        {readyForProduction ? (
          <Alert severity="success" sx={{ mb: 1.5 }}>
            Commissioning is complete for this device. Run production deploy below for the enterprise acceptance gate,
            or continue in Device Control.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Run bring-up and pass strict gate before marking this device as fully commissioned.
          </Alert>
        )}
        {selectedDeviceId && canReadDeployments && (
          <Box sx={{ mb: 2 }}>
            <EnterpriseProductionDeployPanel
              deviceId={selectedDeviceId}
              device={selected}
              variant="compact"
              showPostBringUpCta
            />
          </Box>
        )}
        <Stack direction="row" gap={1} flexWrap="wrap">
          <Button variant="contained" onClick={() => navigate('/devices')}>
            Finish
          </Button>
          <Button variant="outlined" onClick={() => navigate('/preflight')}>
            Open Advanced Preflight
          </Button>
        </Stack>
      </Paper>

      <BringUpDangerZone
        selectedDeviceId={selectedDeviceId}
        canWriteDevices={canWriteDevices}
        decommissionBusy={decommissionBusy}
        decommissionConfirm={decommissionConfirm}
        onConfirmChange={setDecommissionConfirm}
        onFillConfirm={() => selectedDeviceId && setDecommissionConfirm(selectedDeviceId)}
        deleteAfterDecommission={deleteAfterDecommission}
        onDeleteAfterChange={setDeleteAfterDecommission}
        factoryResetOnDecommission={factoryResetOnDecommission}
        onFactoryResetChange={setFactoryResetOnDecommission}
        deleteConfigOnDecommission={deleteConfigOnDecommission}
        onDeleteConfigChange={setDeleteConfigOnDecommission}
        decommissionResult={decommissionResult}
        onClearResult={() => setDecommissionResult(null)}
        jobIsRunning={jobIsRunning}
        onDecommission={runDecommission}
        onDeleteDeviceOnly={runDeleteDeviceOnly}
      />

      <BringUpGuidedRecoveryDialog
        open={guidedRecoveryOpen}
        selectedDeviceId={selectedDeviceId}
        canWriteDevices={canWriteDevices}
        guidedRecoveryBusy={guidedRecoveryBusy}
        guidedRecoveryDeleteAfter={guidedRecoveryDeleteAfter}
        onDeleteAfterChange={setGuidedRecoveryDeleteAfter}
        guidedRecoveryRetryBringUp={guidedRecoveryRetryBringUp}
        onRetryBringUpChange={setGuidedRecoveryRetryBringUp}
        guidedRecoveryError={guidedRecoveryError}
        onClearError={() => setGuidedRecoveryError(null)}
        onClose={closeGuidedRecoveryDialog}
        onRun={() => void runGuidedRecovery()}
      />
        </Grid>
        <Grid item xs={12} lg={4}>
          <SuggestedActionsRail context="bringup" deviceId={selectedDeviceId} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default BringUp;
